import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { RbacApi, type Position } from "./api";

export function Positions() {
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);
  const positions = useQuery({ queryKey: ["positions"], queryFn: RbacApi.listPositions });

  const create = useMutation({
    mutationFn: RbacApi.createPosition,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["positions"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const remove = useMutation({
    mutationFn: RbacApi.deletePosition,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["positions"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [scopeLevel, setScopeLevel] = useState<Position["scopeLevel"]>("branch");

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name) return;
    create.mutate({ name, description: desc, scopeLevel });
    setName(""); setDesc("");
  }

  return (
    <div className="hierarchy">
      <header>
        <h1>Positions</h1>
        <p className="muted">
          A <strong>position</strong> is a job title in the org chart (e.g. "Branch Manager", "District Officer") used
          for reporting lines and labelling the team. It grants <em>no</em> permissions on its own.
        </p>
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Permissions live on <Link to="../roles">Roles</Link>. A typical user has one position (their slot in the org chart)
          and one or more roles (what they're allowed to do). Pick a scope level so the system knows where in the hierarchy
          this title sits.
        </p>
      </header>
      {error && <div role="alert" className="error">{error}</div>}

      <form className="create" onSubmit={submit}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Position name" />
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" />
        <select value={scopeLevel} onChange={(e) => setScopeLevel(e.target.value as Position["scopeLevel"])}>
          <option value="constituency">Constituency</option>
          <option value="ward">Ward</option>
          <option value="branch">Branch</option>
        </select>
        <button type="submit" disabled={create.isPending}>{create.isPending ? "Adding…" : "Add"}</button>
      </form>

      <ul className="tree">
        {positions.data?.items.map((p) => (
          <li key={p.id}>
            <div className="row">
              <span>
                <strong>{p.name}</strong> <span className="muted">({p.scopeLevel})</span>
                <div className="muted" style={{ fontSize: 12 }}>{p.description || "—"}</div>
              </span>
              {/* Delete removed — positions are kept for assignment history. */}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
